import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { apiBaseUrl, authClient } from './auth-client';

type CaptureContext = {
  extra?: Record<string, unknown>;
  tags?: Record<string, string>;
};

function parseSampleRate(value: string | undefined, fallback: number) {
  if (!value) {
    return fallback;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : fallback;
}

function parseBooleanFlag(value: string | undefined) {
  if (!value) {
    return undefined;
  }

  const normalized = value.trim().toLowerCase();

  if (['1', 'true', 'yes', 'on'].includes(normalized)) {
    return true;
  }

  if (['0', 'false', 'no', 'off'].includes(normalized)) {
    return false;
  }

  return undefined;
}

const sentryEnabled = parseBooleanFlag(import.meta.env.VITE_SENTRY_ENABLED)
  ?? (Boolean(import.meta.env.VITE_SENTRY_DSN) && !['test', 'development'].includes(import.meta.env.MODE));
const apiOrigin = new URL(apiBaseUrl, window.location.origin).origin;
const replaysSessionSampleRate = parseSampleRate(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE, 0);
const replaysOnErrorSampleRate = parseSampleRate(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE, 0);
const shouldEnableReplay = replaysSessionSampleRate > 0 || replaysOnErrorSampleRate > 0;
const integrations = [
  Sentry.reactRouterV7BrowserTracingIntegration({
    useEffect,
    useLocation,
    useNavigationType,
    createRoutesFromChildren,
    matchRoutes,
  }),
];

if (shouldEnableReplay) {
  integrations.push(Sentry.replayIntegration());
}

Sentry.init({
  enabled: sentryEnabled,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
  release: import.meta.env.VITE_SENTRY_RELEASE,
  sendDefaultPii: false,
  normalizeDepth: 6,
  tracePropagationTargets: ['localhost', /^\//, apiOrigin],
  tracesSampleRate: parseSampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, sentryEnabled ? 0.1 : 0),
  replaysSessionSampleRate,
  replaysOnErrorSampleRate,
  integrations,
});

export function SentryUserScope() {
  const sessionState = authClient.useSession();

  useEffect(() => {
    const user = sessionState.data?.user;

    if (!user) {
      Sentry.setUser(null);
      return;
    }

    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.name,
    });
  }, [sessionState.data?.user]);

  return null;
}

export function captureFrontendException(error: unknown, context?: CaptureContext) {
  if (!sentryEnabled) {
    return;
  }

  Sentry.withScope((scope) => {
    for (const [key, value] of Object.entries(context?.tags ?? {})) {
      scope.setTag(key, value);
    }

    for (const [key, value] of Object.entries(context?.extra ?? {})) {
      scope.setExtra(key, value);
    }

    Sentry.captureException(error);
  });
}

export { Sentry, sentryEnabled };
